import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClinicBasePath } from '@/hooks/useClinicBasePath';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  Filters,
  ChoiceList,
  InlineStack,
  Text,
  Pagination,
  SkeletonBodyText,
  EmptyState,
  Box,
} from '@shopify/polaris';
import { clinicPortalApi } from '@/services';
import type { Rental, RentalStatus } from '@/types';
import { orderNumber } from '@/utils/orderNumber';

const STATUS_CHOICES = [
  { label: 'Pending', value: 'pending' },
  { label: 'Processing', value: 'processing' },
  { label: 'Ready to Ship', value: 'ready_to_ship' },
  { label: 'Shipped', value: 'shipped' },
  { label: 'In Use', value: 'in_use' },
  { label: 'Extended', value: 'extended' },
  { label: 'Returned', value: 'returned' },
  { label: 'Complete', value: 'complete' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Overdue', value: 'overdue' },
];

const STATUS_TONE: Record<string, 'success' | 'warning' | 'critical' | 'info' | undefined> = {
  pending: undefined,
  processing: 'info',
  ready_to_ship: 'info',
  shipped: 'info',
  in_use: 'success',
  extended: 'warning',
  returned: 'warning',
  complete: 'success',
  cancelled: 'critical',
  refunded: 'critical',
  partial_refund: 'warning',
  overdue: 'critical',
};

function getStatusBadge(status: RentalStatus) {
  const label = status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return <Badge tone={STATUS_TONE[status]}>{label}</Badge>;
}

export default function ClinicOrders() {
  const navigate = useNavigate();
  const base = useClinicBasePath();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['clinic-orders', page, status, searchQuery],
    queryFn: () =>
      clinicPortalApi.getOrders({
        page,
        limit,
        status: status[0],
        search: searchQuery || undefined,
      }),
  });

  const handleStatusChange = useCallback((value: string[]) => {
    setStatus(value);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatus([]);
    setSearchQuery('');
  }, []);

  const filters = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={STATUS_CHOICES}
          selected={status}
          onChange={handleStatusChange}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = status.length > 0
    ? [{ key: 'status', label: `Status: ${status.join(', ')}`, onRemove: () => setStatus([]) }]
    : [];

  const rows = (data?.data || []).map((rental: Rental) => [
    <Button key={rental.id} variant="plain" onClick={() => navigate(`${base}/orders/${rental.id}`)}>
      {orderNumber(rental)}
    </Button>,
    rental.patientName || 'N/A',
    rental.rentalStartDate ? new Date(rental.rentalStartDate).toLocaleDateString() : 'N/A',
    rental.rentalEndDate ? new Date(rental.rentalEndDate).toLocaleDateString() : 'N/A',
    `$${Number(rental.totalAmount).toFixed(2)}`,
    getStatusBadge(rental.rentalStatus),
    <Button key={`view-${rental.id}`} onClick={() => navigate(`${base}/orders/${rental.id}`)}>
      View
    </Button>,
  ]);

  if (error) {
    return (
      <Page title="Orders">
        <Card>
          <EmptyState
            heading="Error loading orders"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Orders">
      <Card padding="0">
        <Box padding="400">
          <Filters
            queryValue={searchQuery}
            queryPlaceholder="Search by order number or patient..."
            filters={filters}
            appliedFilters={appliedFilters}
            onQueryChange={handleSearchChange}
            onQueryClear={() => setSearchQuery('')}
            onClearAll={handleClearFilters}
          />
        </Box>

        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No orders found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Orders will appear here when placed.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['Order #', 'Patient', 'Start Date', 'End Date', 'Amount', 'Status', 'Action']}
              rows={rows}
            />
            {data && data.meta.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {data && (
        <Box paddingBlockStart="400">
          <Text as="p" tone="subdued" alignment="center">
            Showing {data.data.length} of {data.meta.total} orders
          </Text>
        </Box>
      )}
    </Page>
  );
}
